
'use client';

import React from 'react';
import { DemoBanner } from '@/components/DemoBanner';
import { Zap, Users, BarChart3, Link as LinkIcon, Lock, Smartphone, ArrowRight, ChevronRight, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function SaasDemo() {
  const features = [
    { title: "Tezkor vazifalar", desc: "Drag & drop bilan oson boshqarish va guruhlash", icon: Zap },
    { title: "Jamoa hamkorligi", desc: "Real-time muloqot va fayllar almashinuvi", icon: Users },
    { title: "Analitika", desc: "Batafsil hisobotlar va progressni kuzatish", icon: BarChart3 },
    { title: "Integratsiyalar", desc: "100+ ilova va servislar bilan bog'lash", icon: LinkIcon },
    { title: "Xavfsizlik", desc: "Enterprise darajali ma'lumotlar himoyasi", icon: Lock },
    { title: "Mobil ilova", desc: "iOS va Android uchun to'liq moslangan ilova", icon: Smartphone },
  ];

  return (
    <div className="min-h-screen bg-white pt-11 font-sans selection:bg-indigo-600 selection:text-white">
      <DemoBanner />

      {/* NAVIGATION */}
      <nav className="border-b border-gray-50 py-4 sticky top-11 bg-white/80 backdrop-blur-xl z-40">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-2 text-xl font-bold tracking-tighter">
            <div className="w-8 h-8 bg-black text-white rounded-lg flex items-center justify-center">
              <Zap size={20} fill="currentColor" />
            </div>
            CloudFlow
          </div>
          <div className="hidden lg:flex items-center gap-8 text-sm font-medium text-gray-500">
            <a href="#" className="hover:text-black transition-colors">Imkoniyatlar</a>
            <a href="#" className="hover:text-black transition-colors">Narxlar</a>
            <a href="#" className="hover:text-black transition-colors">Integratsiyalar</a>
            <a href="#" className="hover:text-black transition-colors">Blog</a>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-sm font-semibold">Kirish</Button>
            <Button className="bg-black text-white hover:bg-gray-800 rounded-full px-6">Bepul boshlash</Button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="py-24 md:py-40 text-center overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 border border-indigo-100 rounded-full mb-8">
            <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">✨ Yangi: AI yordamchi qo'shildi</span>
          </div>
          
          <h1 className="text-5xl md:text-8xl font-bold tracking-tighter text-gray-900 mb-8 max-w-5xl mx-auto leading-[0.95]">
            Jamoangiz bilan ishlash, hech qachon bunday oson bo'lmagan
          </h1>
          
          <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-12 leading-relaxed">
            Loyihalar, vazifalar, muloqot va hisobotlar — hammasi bitta zamonaviy va tezkor platformada.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-24">
            <Button size="lg" className="rounded-full h-14 px-10 bg-black text-white hover:bg-gray-800 group">
              Bepul sinab ko'ring <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button size="lg" variant="outline" className="rounded-full h-14 px-10 border-gray-200">
              <Play size={16} fill="currentColor" className="mr-2" /> Demo ko'rish
            </Button>
          </div>

          {/* MOCKUP */}
          <div className="relative max-w-6xl mx-auto">
            <div className="absolute inset-0 bg-indigo-600/10 blur-[120px] rounded-full -z-10" />
            <div className="bg-gray-900 rounded-[2rem] p-4 shadow-2xl border border-gray-800 overflow-hidden">
              <div className="flex gap-2 mb-4 px-4">
                <div className="w-3 h-3 rounded-full bg-red-500/50" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                <div className="w-3 h-3 rounded-full bg-green-500/50" />
              </div>
              <div className="bg-white rounded-2xl h-[500px] flex overflow-hidden">
                <div className="w-64 bg-gray-50 border-r border-gray-100 p-6 flex flex-col gap-6">
                   <div className="w-32 h-4 bg-gray-200 rounded" />
                   <div className="space-y-3">
                      {[1,2,3,4].map(i => <div key={i} className="w-full h-3 bg-gray-100 rounded" />)}
                   </div>
                </div>
                <div className="flex-1 p-8 grid grid-cols-3 gap-6">
                   {[1,2,3].map(i => (
                     <div key={i} className="space-y-4">
                        <div className="w-24 h-4 bg-gray-100 rounded" />
                        <div className="bg-gray-50 h-32 rounded-xl" />
                        <div className="bg-gray-50 h-24 rounded-xl" />
                     </div>
                   ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white p-10 rounded-3xl border border-gray-100 hover:shadow-xl hover:shadow-indigo-900/5 transition-all">
                  <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center mb-8">
                    <Icon size={24} />
                  </div>
                  <h3 className="text-xl font-bold mb-4">{f.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <section className="py-24 border-y border-gray-100">
        <div className="container mx-auto px-6">
          <p className="text-center text-xs font-bold text-gray-400 uppercase tracking-[0.3em] mb-12">Bizga ishongan kompaniyalar</p>
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-24 opacity-30 grayscale">
            {['LOGOTECH', 'QUANTUM', 'NEXUS', 'VIBE', 'MINDSET'].map(l => (
              <span key={l} className="text-2xl font-black tracking-tighter">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-12 pt-12 border-t border-gray-50">
            <div className="flex items-center gap-2 text-xl font-bold tracking-tighter">
              <div className="w-8 h-8 bg-black text-white rounded-lg flex items-center justify-center">
                <Zap size={20} fill="currentColor" />
              </div>
              CloudFlow
            </div>
            <div className="flex gap-8 text-sm font-medium text-gray-500">
              <a href="#" className="hover:text-black">Terms</a>
              <a href="#" className="hover:text-black">Privacy</a>
              <a href="#" className="hover:text-black">Security</a>
            </div>
            <p className="text-xs text-gray-400">© 2026 CloudFlow. Barcha huquqlar himoyalangan.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
